import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMemo, useState } from 'react';
import { MdOutlineSendTimeExtension } from 'react-icons/md';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useCityContext } from '../../../contexts/CityProvider';
import useCitiesActions from '../../../hooks/APIHooks/users/useCitiesActions';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { CityReqBody } from '../../../types/cities';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import SelectedCitiesTable from '../../SetupSubPageComps/CitiesComps/SelectedCitiesTable';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AddCityModal = ({
  handleModalClose,
}: {
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // integration of custom API hooks here
  const { getProvinceListHandler } = useProvincesActions('provinceList');
  const { addNewCityHandler } = useCitiesActions('');

  // integration of context hooks here
  const { cityList, selectedCities, setSelectedCities } = useCityContext();
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // formatting dropdown options here
  const provinceOptions = useMemo(() => {
    if (!getProvinceListHandler.data?.is_success) return [];

    return getProvinceListHandler.data.data.map((province) => ({
      id: province.id,
      name: province.province_name,
      code: province.province_code,
    }));
  }, [getProvinceListHandler.data]);

  // formatting dropdown option descriptions here
  const provinceDescriptionOptions = useMemo(() => {
    if (!getProvinceListHandler.data?.is_success) return [];

    return getProvinceListHandler.data.data.map(
      (province) => `(${province.country_name})`
    );
  }, [getProvinceListHandler.data]);

  // handler function to handle add city feature declared here
  const addCityHandler = async () => {
    try {
      setIsSubmitting(true);

      await addNewCityHandler
        .mutateAsync({
          body: selectedCities.map((city) => ({
            province: city.province,
            city_name: city.city_name,
            created_by: city.created_by,
          })),
        })
        .then((res) => {
          if (res.is_success) {
            toast.success('New City Added!!');

            handleModalClose();
            setSelectedCities([]);
            queryClient.invalidateQueries({ queryKey: ['city-list'] });
          } else {
            toast.error(res.message || 'City Adding Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsSubmitting(false);
    }
  };

  // function to add city to selected cities list declared here
  const addCityToSelectedList = (values: any, actions: FormikHelpers<any>) => {
    // merging city list and newly added cities here
    const allCities = cityList
      .map<CityReqBody>((city) => ({
        province: city.province,
        province_name: city.province_name,
        city_name: city.city_name,
        created_by: city.created_by,
      }))
      .concat(selectedCities);

    // checking if city already exists or not here
    const existingCity = allCities.find(
      (city) =>
        city.province === values.province.id &&
        city.city_name.toLowerCase() === values.city.toLowerCase()
    );

    if (!existingCity) {
      const city: CityReqBody = {
        province: values.province.id,
        province_name: values.province.name,
        city_name: values.city,
        created_by: currentUser?.username,
      };

      setSelectedCities((state) => [...state, city]);

      actions.setFieldValue('city', '');
    } else {
      toast.warning('City Already Exists!!');
    }
  };

  console.log('SELECTED CITY: ', selectedCities);

  // rendering add city modal component here
  return (
    <ParentModal
      onClick={() => {
        handleModalClose();
        setSelectedCities([]);
      }}
    >
      <ModalTitle titleText='add city' />

      <Formik
        initialValues={{ province: SELECT_FIELD_DEFAULT_VALUE, city: '' }}
        onSubmit={addCityToSelectedList}
      >
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaFormSelect
              name='province'
              label='Select Province'
              options={provinceOptions}
              optionDescription={provinceDescriptionOptions}
              defaultValue={props.values.province}
              showSearchInput
              isRequired
            />

            <AcademiaInputField
              type='text'
              name='city'
              label='City Name'
              placeholder='Enter city name here...'
              isRequired
            />

            {selectedCities.length ? <SelectedCitiesTable /> : null}

            <div className='grid grid-cols-2 gap-4'>
              <AcademiaButton
                title={
                  props.values.province.id === -1 || !props.values.city
                    ? 'Please, select a province & add a city first!'
                    : ''
                }
                type='submit'
                variant='info'
                icon={<TbSquareRoundedPlusFilled className='size-6' />}
                isDisabled={
                  props.values.province.id === -1 || !props.values.city
                }
                fullWidth
              >
                Add City
              </AcademiaButton>

              <AcademiaButton
                title={
                  !selectedCities.length
                    ? 'Please, select some cities first!'
                    : ''
                }
                type='button'
                onClick={addCityHandler}
                variant='premium'
                icon={<MdOutlineSendTimeExtension className='size-6' />}
                isLoading={isSubmitting}
                isDisabled={!selectedCities.length}
                fullWidth
              >
                Submit
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default AddCityModal;
